import type { api } from '@guild/core'

import type { App } from './app.ts'
import { createStore } from './store.ts'

/**
 * The post, once, shared (#103).
 *
 * `[ The Post ]` has to know whether anything is waiting before the player
 * goes and looks, and the post screen has to know what is waiting when they
 * do. Both read this: one fetch of the sealed summaries on boot, kept for the
 * session, and opening a letter clears its own mark here rather than by asking
 * the server again.
 *
 * A sealed summary is a code name, two days and two flags, which is the whole
 * of what the contract will say about a letter before it is opened. Holding
 * that in the shell reveals nothing further -- it is the same list the post
 * screen was already fetching for itself.
 */

export type InboxState = {
  letters: readonly api.ApiInboxSummary[]
  loaded: boolean
}

export const inboxStore = createStore<InboxState>({ letters: [], loaded: false })

/** How many envelopes are still sealed. */
export const unopenedCount = (): number =>
  inboxStore.get().letters.filter((letter) => !letter.opened).length

/**
 * Which player these summaries are about. Bumped by every clear, so an answer
 * to the last player's question is dropped rather than shown to the next (#115).
 */
let generation = 0

/** Fetch the summaries. A failure leaves the mark off rather than shouting. */
export async function loadInbox(app: App): Promise<void> {
  const mine = generation
  const result = await app.api.post()
  if (!result.ok || generation !== mine) return
  inboxStore.set({ letters: result.value.letters, loaded: true })
}

/** Forget everything when the player changes: this post is not the next one's. */
export function clearInbox(): void {
  generation += 1
  inboxStore.reset()
}

/** Patch one summary in place, so the mark clears without another round trip. */
export function updateSummary(publicId: string, patch: Partial<api.ApiInboxSummary>): void {
  inboxStore.set({
    letters: inboxStore
      .get()
      .letters.map((letter) => (letter.publicId === publicId ? { ...letter, ...patch } : letter)),
  })
}
